/**
 * useDocumentDialog Hook
 * Main orchestration hook for DocumentDialog
 * Combines file upload, form state, and submission logic
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentCreate } from '../../api/ApiClient';
import { useDocument, useUploadDocument, useUpdateDocument, useUpdateDocumentFile } from '../api/useDocuments';
import { log } from '../../utils/errors/logger';
import { useFileUpload } from './useFileUpload';
import { useDocumentForm } from './useDocumentForm';
import { CreateDocTabValue, UseDocumentDialogOptions, UseDocumentDialogReturn } from '../../components/documents/DocumentDialog/types';

/**
 * Get source type based on tab value
 */
const getSourceTypeForTab = (tabValue: number): string => {
  switch (tabValue) {
    case 0: return 'manual'; // Text content
    case 1: return 'file';   // File upload
    case 2: return 'url';    // URL import
    default: return 'manual';
  }
};

const parseCsvIds = (value: string): string[] =>
  value.split(',').map(item => item.trim()).filter(Boolean);

/**
 * Main hook for DocumentDialog logic
 */
export const useDocumentDialog = ({
  open,
  editingDoc,
  defaultProvider,
  onClose,
  onSubmit
}: UseDocumentDialogOptions): UseDocumentDialogReturn => {
  const { t } = useTranslation();

  // TanStack Query mutations
  const uploadDocumentMutation = useUploadDocument();
  const updateDocumentMutation = useUpdateDocument();
  const updateDocumentFileMutation = useUpdateDocumentFile();

  // Tab state
  const [activeTab, setActiveTab] = useState<CreateDocTabValue>(0);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Use custom hooks
  const fileUpload = useFileUpload();
  const { data: fullDocument, isLoading: isLoadingDocument } = useDocument(
    editingDoc?.document_id || (editingDoc as any)?.id || ''
  );

  const documentForm = useDocumentForm({
    editingDoc,
    defaultProvider,
    fullDocument,
    selectedFile: fileUpload.selectedFile
  });

  /**
   * Initialize/reset state when dialog opens/closes
   */
  useEffect(() => {
    if (open) {
      // Reset submission state
      setSubmitError(null);
      setIsSubmitting(false);

      // Set tab based on document source type (edit mode)
      if (editingDoc) {
        console.log('[DocumentDialog] Setting tab for editing document:', {
          title: editingDoc.title,
          source_type: editingDoc.source_type,
          file_type: editingDoc.file_type
        });
        if (editingDoc.source_type === 'file') {
          console.log('[DocumentDialog] Setting tab to 1 (file)');
          setActiveTab(1); // File tab
        } else if (editingDoc.source_type === 'url') {
          console.log('[DocumentDialog] Setting tab to 2 (url)');
          setActiveTab(2); // URL tab
        } else {
          // Text tab for 'manual', 'custom', or any other type
          console.log('[DocumentDialog] Setting tab to 0 (text) for source_type:', editingDoc.source_type);
          setActiveTab(0);
        }
      } else {
        setActiveTab(0); // Default to text tab for new documents
        fileUpload.resetFile();
      }
    }
    // fileUpload is intentionally excluded to prevent effect from re-running on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingDoc]);

  /**
   * Reset form state when dialog opens/closes
   * This mimics the FAQ dialog pattern that works correctly
   */
  useEffect(() => {
    if (open) {
      // When opening, ensure form is reset with current data
      documentForm.resetForm();
    } else {
      // When closing, explicitly clean up all state
      documentForm.resetForm();
      fileUpload.resetFile();
      setActiveTab(0);
      setSubmitError(null);
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingDoc]);

  /**
   * Handle tab change
   */
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue as CreateDocTabValue);
  }, []);

  /**
   * Validate form - memoized to reactively update when dependencies change
   */
  const isValid = useMemo((): boolean => {
    // Title and visibility are always required
    if (!documentForm.formState.title || !documentForm.formState.visibility) {
      return false;
    }

    if (editingDoc) {
      // **EDIT MODE** - Allow metadata-only edits for ALL document types
      // Users can update title, visibility, sharing without changing content
      // No additional validation required beyond title and visibility

    } else {
      // **CREATE MODE** - Strict validation required
      if (activeTab === 0 && !documentForm.formState.content) {
        return false;
      }
      if (activeTab === 1 && !fileUpload.selectedFile) {
        return false;
      }
      if (activeTab === 2 && !documentForm.formState.url) {
        return false;
      }
    }

    // Prevent submission during upload or double-submit
    return !fileUpload.uploadingFile && !isSubmitting;
  }, [documentForm.formState, editingDoc, activeTab, fileUpload.selectedFile, fileUpload.uploadingFile, isSubmitting]);

  const aclSettings = useMemo(() => ({
    visibility: documentForm.formState.visibility,
    shared_with_users: parseCsvIds(documentForm.formState.shared_with_users),
    shared_with_groups: parseCsvIds(documentForm.formState.shared_with_groups)
  }), [
    documentForm.formState.visibility,
    documentForm.formState.shared_with_users,
    documentForm.formState.shared_with_groups
  ]);

  /**
   * Handle document submission
   */
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (editingDoc) {
        // **EDIT MODE**
        if (editingDoc.source_type === 'file' && fileUpload.selectedFile) {
          // File replacement - new file selected
          fileUpload.setUploadingFile(true);

          // Simulate upload progress
          const progressInterval = setInterval(() => {
            fileUpload.setUploadProgress(prev => {
              const newProgress = prev + Math.random() * 15;
              return newProgress >= 90 ? 90 : newProgress;
            });
          }, 300);

          try {
            await updateDocumentFileMutation.mutateAsync({
              documentId: editingDoc.document_id || (editingDoc as any).id,
              file: fileUpload.selectedFile,
              title: documentForm.formState.title,
              aclSettings,
              textExtractionProvider: documentForm.formState.text_extraction_provider
            });
            clearInterval(progressInterval);
            fileUpload.setUploadProgress(100);
            setTimeout(() => fileUpload.setUploadingFile(false), 500);
          } catch (error) {
            clearInterval(progressInterval);
            fileUpload.setUploadingFile(false);
            throw error;
          }
        } else {
          // Update metadata only (no new file OR text/url documents)
          const updateData: any = {
            title: documentForm.formState.title,
            visibility: documentForm.formState.visibility,
            source_type: editingDoc.source_type, // Preserve existing source_type
            file_type: editingDoc.file_type, // Preserve existing file_type
            shared_with_users: aclSettings.shared_with_users,
            shared_with_groups: aclSettings.shared_with_groups
          };

          // Add content for text documents (manual or custom)
          const isTextDocument = editingDoc.source_type === 'manual' || editingDoc.source_type === 'custom';
          if (isTextDocument) {
            updateData.content = documentForm.formState.content;
          }

          // Add URL for URL documents
          if (editingDoc.source_type === 'url') {
            updateData.url = documentForm.formState.url;
          }

          await updateDocumentMutation.mutateAsync({
            documentId: editingDoc.document_id!,
            data: updateData
          });
        }
      } else {
        // **CREATE MODE**
        if (activeTab === 0) {
          // Text content
          if (!documentForm.formState.content) {
            throw new Error(t('documents.notification.contentRequired'));
          }
          const docData: DocumentCreate = {
            title: documentForm.formState.title,
            content: documentForm.formState.content,
            visibility: documentForm.formState.visibility as any,
            source_type: getSourceTypeForTab(0) as any,
            url: '',
            shared_with_users: aclSettings.shared_with_users,
            shared_with_groups: aclSettings.shared_with_groups
          };
          await onSubmit(docData, false);
        } else if (activeTab === 1) {
          // File upload
          if (!fileUpload.selectedFile) {
            throw new Error(t('documents.notification.fileRequired'));
          }
          fileUpload.setUploadingFile(true);

          // Simulate upload progress
          const progressInterval = setInterval(() => {
            fileUpload.setUploadProgress(prev => {
              const newProgress = prev + Math.random() * 15;
              return newProgress >= 90 ? 90 : newProgress;
            });
          }, 300);

          try {
            await uploadDocumentMutation.mutateAsync({
              file: fileUpload.selectedFile,
              title: documentForm.formState.title,
              aclSettings,
              textExtractionProvider: documentForm.formState.text_extraction_provider
            });
            clearInterval(progressInterval);
            fileUpload.setUploadProgress(100);
            setTimeout(() => fileUpload.setUploadingFile(false), 500);
          } catch (error) {
            clearInterval(progressInterval);
            fileUpload.setUploadingFile(false);
            throw error;
          }
        } else if (activeTab === 2) {
          // URL content
          if (!documentForm.formState.url) {
            throw new Error(t('documents.notification.urlRequired'));
          }
          const docData: DocumentCreate = {
            title: documentForm.formState.title,
            url: documentForm.formState.url,
            visibility: documentForm.formState.visibility as any,
            source_type: getSourceTypeForTab(2) as any,
            content: '',
            shared_with_users: aclSettings.shared_with_users,
            shared_with_groups: aclSettings.shared_with_groups
          };
          await onSubmit(docData, false);
        }
      }

      onClose();
    } catch (error: any) {
      log.error('Error submitting document', { error: error.message });
      setSubmitError(error.message || t('documents.notification.saveFailed', 'Failed to save document'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    editingDoc,
    activeTab,
    documentForm.formState,
    fileUpload,
    uploadDocumentMutation,
    updateDocumentMutation,
    updateDocumentFileMutation,
    aclSettings,
    onSubmit,
    onClose,
    t
  ]);

  return {
    // Mode
    isEditMode: Boolean(editingDoc),

    // Form state
    formState: documentForm.formState,
    handleFormChange: documentForm.handleFormChange,
    handleSelectChange: documentForm.handleSelectChange,

    // File handling
    selectedFile: fileUpload.selectedFile,
    handleFileSelect: fileUpload.handleFileSelect,
    handleFileBrowse: fileUpload.handleFileBrowse,
    handleFileRemove: fileUpload.handleFileRemove,
    uploadProgress: fileUpload.uploadProgress,
    uploadingFile: fileUpload.uploadingFile,
    fileInputRef: fileUpload.fileInputRef,
    fileError: fileUpload.fileError,
    maxFileSizeMB: fileUpload.maxFileSizeMB,

    // Tab state
    activeTab,
    handleTabChange,

    // Submission
    handleSubmit,
    isSubmitting,
    submitError,

    // Validation
    isValid,

    // Data loading
    fullDocument,
    isLoadingDocument,

    // Visibility and sharing
    handleVisibilityChange: documentForm.handleVisibilityChange,
    handleSharingChange: documentForm.handleSharingChange
  };
};
